import { makeStyles } from '@material-ui/core/styles'
import CustomField from 'elements/field/CustomField'
import DependentInput from 'elements/input/DependentInput'
import InfoTooltip from 'elements/tooltip/InfoTooltip'
import { Grid } from 'opensolar-ui'
import { NumberInput, SelectInput, useTranslate } from 'react-admin'
import { validRange } from 'validations'
import {
  FieldWithTooltipContainer,
  FieldWrapper,
  SubsectionHeader,
  TooltipIconWrapper,
} from '../ProjectConfigurations.styles'
import { InverterReplacementModellingTooltip } from './InverterReplacementModellingTooltip'
import { ReplacementCostMethodFields } from './ReplacementCostMethodFields'

const useTooltipStyles = makeStyles((theme) => ({
  tooltipWide: {
    maxWidth: '90vw',
    minWidth: '280px !important',
    [theme.breakpoints.up('sm')]: {
      maxWidth: '600px !important',
      minWidth: '350px !important',
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: '700px !important',
      minWidth: '400px !important',
    },
  },
}))

interface InverterReplacementSectionProps {
  allowEdit: boolean
  [key: string]: any
}

export const InverterReplacementSection = ({ allowEdit, ...props }: InverterReplacementSectionProps) => {
  const translate = useTranslate()
  const tooltipClasses = useTooltipStyles()

  return (
    <>
      <SubsectionHeader>{translate('Inverter')}</SubsectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <FieldWithTooltipContainer>
            <FieldWrapper style={{ width: '100%' }}>
              <CustomField
                component={SelectInput}
                name={'end_of_life_config.inverter_replacement_modelling'}
                source="end_of_life_config.inverter_replacement_modelling"
                label="Inverter Replacement Modelling"
                resource="project_configurations"
                choices={[
                  { id: 0, name: 'Extend Beyond Warranty' },
                  { id: 1, name: 'Replace' },
                ]}
                disabled={!allowEdit}
                fullWidth
                defaultValue={0}
              />
            </FieldWrapper>
            <TooltipIconWrapper>
              <InfoTooltip
                placement="right"
                title={<InverterReplacementModellingTooltip />}
                severity="info"
                size="small"
                classes={{
                  tooltip: tooltipClasses.tooltipWide,
                }}
              />
            </TooltipIconWrapper>
          </FieldWithTooltipContainer>
        </Grid>
      </Grid>
      <DependentInput dependsOn="end_of_life_config.inverter_replacement_modelling" resolve={(val) => val === 1}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FieldWithTooltipContainer>
              <FieldWrapper style={{ width: '100%' }}>
                <CustomField
                  component={SelectInput}
                  name={'end_of_life_config.inverter_end_of_life_definition'}
                  source="end_of_life_config.inverter_end_of_life_definition"
                  label="Inverter End of Life Definition"
                  resource="project_configurations"
                  choices={[
                    { id: 0, name: 'Inverter Warranty' },
                    { id: 1, name: 'End of Life Years Override' },
                  ]}
                  disabled={!allowEdit}
                  fullWidth
                  defaultValue={0}
                />
              </FieldWrapper>
            </FieldWithTooltipContainer>
          </Grid>
          <DependentInput
            dependsOn="end_of_life_config.inverter_end_of_life_definition"
            resolve={(val) => val === 1}
          >
            <Grid item xs={12} sm={6} md={4}>
              <FieldWrapper style={{ width: '100%' }}>
                <NumberInput
                  style={{ width: '100%' }}
                  name="end_of_life_config.inverter_end_of_life_years_override"
                  source="end_of_life_config.inverter_end_of_life_years_override"
                  label="Inverter End of Life Years Override"
                  defaultValue={10}
                  endAdornment={translate('years')}
                  disabled={!allowEdit}
                  InputLabelProps={{ shrink: true }}
                  helperText={translate('Specify the number of years for inverter end of life (1-25 years)')}
                  validate={validRange(1, 25, true, { fieldName: 'Inverter End of Life Years Override' })}
                  fullWidth
                />
              </FieldWrapper>
            </Grid>
          </DependentInput>
        </Grid>
        <ReplacementCostMethodFields
          componentType="inverter"
          costMethodField="inverter_replacement_cost_method"
          inflationField="inverter_replacement_cost_inflation_annual"
          fixedValueField="inverter_replacement_cost_value"
          allowEdit={allowEdit}
          {...props}
        />
      </DependentInput>
    </>
  )
}
